import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, GeoJSON } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "./Dashboard.css";
import DijonCard from "./DijonCard";

// Campsite types from camping-ne.json (t: 'tent' | 'rv').
const TYPES = {
  tent: { key: "tent", label: "Campground", color: "#2e7d32" },
  rv: { key: "rv", label: "RV / Caravan", color: "#1976d2" },
};

// Shaded BLM land overlay (blm-land.json). BLM = mostly western public land,
// where dispersed camping is often allowed.
const BLM_COLOR = "#caa24a";
const blmStyle = {
  color: "#8a6d1f",
  weight: 0.5,
  fillColor: BLM_COLOR,
  fillOpacity: 0.35,
};

function DijonCamping(props) {
  const [spots, setSpots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [visible, setVisible] = useState({ tent: true, rv: true });
  const [sel, setSel] = useState(null); // one shared popup for perf (2.5k points)

  const [blm, setBlm] = useState(null);
  const [blmOn, setBlmOn] = useState(true);

  useEffect(() => {
    let alive = true;
    fetch(`${process.env.PUBLIC_URL}/camping-ne.json`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (alive) {
          setSpots(data);
          setLoading(false);
        }
      })
      .catch((e) => {
        if (alive) {
          setErr(e.message);
          setLoading(false);
        }
      });
    return () => {
      alive = false;
    };
  }, []);

  // BLM land loads independently — a failure here must not blank the campgrounds.
  useEffect(() => {
    let alive = true;
    fetch(`${process.env.PUBLIC_URL}/blm-land.json`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (alive && data) setBlm(data);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const shown = useMemo(() => spots.filter((s) => visible[s.t]), [spots, visible]);

  const toggle = (k) => setVisible((v) => ({ ...v, [k]: !v[k] }));

  const onEachBlm = (feature, layer) => {
    const p = (feature && feature.properties) || {};
    const name = p.ADMIN_UNIT_NAME || "BLM land";
    const st = p.ADMIN_ST ? ` &middot; ${p.ADMIN_ST}` : "";
    layer.bindPopup(
      `<strong>${name}</strong><br/><span>Bureau of Land Management${st}</span>`
    );
  };

  return (
    <div id="camping">
      <DijonCard
        width={"820px"}
        height={"680px"}
        startMaximized={true}
        activeCards={props.activeCards}
        setActiveCards={props.setActiveCards}
        name={"camping"}
        content={
          <div style={{ height: "100%", width: "100%", position: "relative" }}>
            <MapContainer
              preferCanvas={true}
              center={[39.5, -98.35]}
              zoom={4}
              scrollWheelZoom={true}
              style={{ height: "100%", width: "100%" }}
            >
              <TileLayer
                attribution='&copy; OpenStreetMap contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                maxZoom={19}
              />
              {blmOn && blm && (
                <GeoJSON data={blm} style={blmStyle} onEachFeature={onEachBlm} />
              )}
              {shown.map((s, i) => (
                <CircleMarker
                  key={i}
                  center={[s.y, s.x]}
                  radius={5}
                  pathOptions={{
                    color: "#ffffff",
                    weight: 1,
                    fillColor: (TYPES[s.t] || TYPES.tent).color,
                    fillOpacity: 0.9,
                  }}
                  eventHandlers={{ click: () => setSel(s) }}
                />
              ))}
              {sel && (
                <Popup position={[sel.y, sel.x]} eventHandlers={{ remove: () => setSel(null) }}>
                  <strong>{sel.n}</strong>
                  <br />
                  <span style={{ color: (TYPES[sel.t] || TYPES.tent).color, fontWeight: 600 }}>
                    {(TYPES[sel.t] || TYPES.tent).label}
                  </span>
                  {sel.s ? <> &middot; {sel.s}</> : null}
                  {sel.f ? (
                    <>
                      <br />
                      <span style={{ fontSize: "12px" }}>Fee</span>
                    </>
                  ) : null}
                  {sel.w ? (
                    <>
                      <br />
                      <a href={sel.w} target="_blank" rel="noopener noreferrer">
                        website
                      </a>
                    </>
                  ) : null}
                </Popup>
              )}
            </MapContainer>

            {/* legend / filter / count */}
            <div
              style={{
                position: "absolute",
                top: 10,
                right: 10,
                zIndex: 1000,
                background: "rgba(0,0,0,0.72)",
                color: "#fff",
                borderRadius: 8,
                padding: "8px 10px",
                fontSize: 12,
                lineHeight: 1.6,
                minWidth: 120,
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 4 }}>
                {loading ? "Loading…" : `${shown.length.toLocaleString()} spots`}
              </div>
              {Object.values(TYPES).map((t) => (
                <div
                  key={t.key}
                  onClick={() => toggle(t.key)}
                  title="Click to show/hide"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    cursor: "pointer",
                    opacity: visible[t.key] ? 1 : 0.4,
                    userSelect: "none",
                  }}
                >
                  <span
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      background: t.color,
                      border: "1px solid #fff",
                      flexShrink: 0,
                    }}
                  />
                  {t.label}
                </div>
              ))}
              <div
                onClick={() => setBlmOn((v) => !v)}
                title={blm ? "Click to show/hide" : "BLM land still loading…"}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  cursor: "pointer",
                  opacity: blmOn && blm ? 1 : 0.4,
                  userSelect: "none",
                }}
              >
                <span
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: 3,
                    background: BLM_COLOR,
                    border: "1px solid #fff",
                    flexShrink: 0,
                  }}
                />
                BLM land
              </div>
              {err && <div style={{ color: "#f99" }}>load failed: {err}</div>}
              <div style={{ color: "#aaa", fontSize: 10, marginTop: 4 }}>
                data: OpenStreetMap &middot; BLM
              </div>
            </div>
          </div>
        }
      />
    </div>
  );
}

export default DijonCamping;
